package com.sweetlime.bingeamigo.presentation.features.xplog

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.XpLogEntry
import com.sweetlime.bingeamigo.presentation.components.relativeTime
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.service.XpService

@Composable
fun XpLogContent(
    viewModel: XpLogViewModel,
    totalXp: Int,
    level: Int,
    onBack: () -> Unit
) {
    val entries by viewModel.entries.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    val nextLevelXp = XpService.xpRequired(level + 1)
    val currentLevelXp = XpService.xpRequired(level)
    val xpNeededForLevel = (nextLevelXp - currentLevelXp).coerceAtLeast(1)
    val progress = ((totalXp - currentLevelXp).toFloat() / xpNeededForLevel.toFloat()).coerceIn(0f, 1f)

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 40.dp)
        ) {
            item {
                Spacer(modifier = Modifier.height(130.dp))
            }

            item {
                XpSummaryCard(
                    totalXp = totalXp,
                    level = level,
                    progress = progress,
                    nextLevelXp = nextLevelXp,
                    entryCount = entries.size
                )
                Spacer(modifier = Modifier.height(20.dp))
            }

            if (isLoading) {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 40.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = BrandColors.Secondary)
                    }
                }
            } else if (entries.isEmpty()) {
                item {
                    Text(
                        text = "No XP earned yet — start watching!",
                        color = Color.Gray,
                        modifier = Modifier.padding(horizontal = 20.dp)
                    )
                }
            } else {
                items(entries) { entry ->
                    XpLogEntryCard(entry)
                }
            }
        }

        IconButton(
            onClick = onBack,
            modifier = Modifier
                .statusBarsPadding()
                .padding(start = 20.dp, top = 12.dp)
                .background(Color.Black.copy(alpha = 0.5f), CircleShape)
                .align(Alignment.TopStart)
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                contentDescription = "Back",
                tint = Color.White
            )
        }
    }
}

@Composable
private fun XpSummaryCard(
    totalXp: Int,
    level: Int,
    progress: Float,
    nextLevelXp: Int,
    entryCount: Int
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp)
            .clip(RoundedCornerShape(20.dp))
            .background(BrandColors.Surface)
            .padding(horizontal = 20.dp, vertical = 16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(
                    text = "Level $level",
                    color = Color.White,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp
                )
                Text(
                    text = XpService.levelName(level),
                    color = BrandColors.Secondary,
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.Medium
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = "$totalXp XP",
                    color = BrandColors.Primary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp
                )
                Text(
                    text = "$entryCount actions",
                    color = Color.Gray,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
        Spacer(modifier = Modifier.height(12.dp))
        LinearProgressIndicator(
            progress = { progress },
            modifier = Modifier.fillMaxWidth(),
            color = BrandColors.Secondary,
            trackColor = Color.White.copy(alpha = 0.1f)
        )
        Spacer(modifier = Modifier.height(6.dp))
        Text(
            text = "$totalXp / $nextLevelXp XP to next level",
            color = Color.White.copy(alpha = 0.6f),
            style = MaterialTheme.typography.bodySmall
        )
    }
}

@Composable
private fun XpLogEntryCard(entry: XpLogEntry) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp, vertical = 5.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(BrandColors.Card)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(46.dp)
                .clip(RoundedCornerShape(12.dp))
                .background(actionColor(entry.action).copy(alpha = 0.15f)),
            contentAlignment = Alignment.Center
        ) {
            Text(text = actionEmoji(entry.action), fontSize = 20.sp)
        }

        Spacer(modifier = Modifier.width(14.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = actionDisplayName(entry.action),
                color = Color.White,
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp
            )
            Spacer(modifier = Modifier.height(2.dp))
            val timestampMillis = (entry.timestamp?.seconds ?: 0L) * 1000L
            Text(
                text = relativeTime(timestampMillis),
                color = Color.Gray,
                fontSize = 13.sp
            )
        }

        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(20.dp))
                .background(BrandColors.Primary.copy(alpha = 0.15f))
                .padding(horizontal = 10.dp, vertical = 4.dp)
        ) {
            Text(
                text = "+${entry.xp} XP",
                color = BrandColors.Primary,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp
            )
        }
    }
}

private fun actionDisplayName(action: String): String = when (action) {
    "watchlist_add_movie" -> "Added Movie to Watchlist"
    "watched_movie"       -> "Marked Movie as Watched"
    "favorite_movie"      -> "Favorited a Movie"
    "rated_movie"         -> "Rated a Movie"
    "reviewed_movie"      -> "Reviewed a Movie"
    "tracking_add_show"   -> "Added Show to Watchlist"
    "episode_watched"     -> "Watched an Episode"
    "season_completed"    -> "Completed a Season"
    "show_completed"      -> "Completed a Show"
    "favorite_show"       -> "Favorited a Show"
    "rated_show"          -> "Rated a Show"
    "reviewed_show"       -> "Reviewed a Show"
    "friend_added"        -> "Added a Friend"
    "reaction_received"   -> "Received a Reaction"
    "streak_7day"         -> "7-Day Streak"
    else                  -> action.replace("_", " ").replaceFirstChar { it.uppercase() }
}

private fun actionEmoji(action: String): String = when (action) {
    "watchlist_add_movie", "tracking_add_show" -> "🎯"
    "watched_movie"                             -> "✅"
    "favorite_movie", "favorite_show"           -> "❤️"
    "rated_movie", "rated_show"                 -> "⭐"
    "reviewed_movie", "reviewed_show"           -> "✍️"
    "episode_watched"                           -> "▶️"
    "season_completed"                          -> "🏁"
    "show_completed"                            -> "🏆"
    "friend_added"                              -> "🤝"
    "reaction_received"                         -> "💬"
    "streak_7day"                               -> "🔥"
    else                                        -> "⚡"
}

private fun actionColor(action: String): Color = when {
    action.contains("watch") || action.contains("episode") -> BrandColors.Secondary
    action.contains("favorite")                            -> BrandColors.Favorite
    action.contains("rate") || action.contains("review")   -> BrandColors.Primary
    action == "show_completed" || action == "season_completed" -> Color(0xFF4CAF50)
    action == "streak_7day"                                -> Color(0xFFFF6B35)
    else                                                   -> Color.Gray
}